/**
 * Centralized Logging Service
 * 
 * Provides structured logging with log levels, conditional logging for production,
 * and integration with error tracking services.
 */

import { Platform } from 'react-native';
import Constants from 'expo-constants';

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4,
}

type LogContext = Record<string, any>;

class Logger {
  private logLevel: LogLevel;
  private isDevelopment: boolean;
  private errorTrackingService: any = null;

  constructor() {
    // Determine environment
    this.isDevelopment = __DEV__ || process.env.NODE_ENV === 'development';
    
    // Set log level based on environment
    // In production, only log WARN and ERROR
    this.logLevel = this.isDevelopment ? LogLevel.DEBUG : LogLevel.WARN;
    
    // Initialize error tracking service (Sentry) if available
    this.initializeErrorTracking();
  }

  private initializeErrorTracking() {
    // Lazy load Sentry to avoid bundling in production if not needed
    try {
      // Sentry will be initialized separately via initErrorTracking()
      // This allows for conditional loading
    } catch (error) {
      // Error tracking not available
    }
  }

  /**
   * Set the error tracking service (e.g., Sentry)
   */
  setErrorTrackingService(service: any) {
    this.errorTrackingService = service;
  }

  /**
   * Set the minimum log level
   */
  setLogLevel(level: LogLevel) {
    this.logLevel = level;
  }

  /**
   * Check if a log level should be logged
   */
  private shouldLog(level: LogLevel): boolean {
    return level >= this.logLevel;
  }

  /**
   * Format log message with context
   */
  private formatMessage(level: string, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString();
    const contextStr = context ? ` ${JSON.stringify(context)}` : '';
    return `[${timestamp}] [${level}] ${message}${contextStr}`;
  }

  /**
   * Log debug message (only in development)
   */
  debug(message: string, context?: LogContext): void {
    if (!this.shouldLog(LogLevel.DEBUG)) return;
    
    if (this.isDevelopment) {
      console.debug(this.formatMessage('DEBUG', message, context));
    }
  }

  /**
   * Log info message
   */
  info(message: string, context?: LogContext): void {
    if (!this.shouldLog(LogLevel.INFO)) return;
    
    if (this.isDevelopment) {
      console.info(this.formatMessage('INFO', message, context));
    } else {
      // In production, send to analytics/logging service
      this.sendToLoggingService('info', message, context);
    }
  }

  /**
   * Log warning message
   */
  warn(message: string, context?: LogContext, error?: Error): void {
    if (!this.shouldLog(LogLevel.WARN)) return;
    
    const formatted = this.formatMessage('WARN', message, context);
    
    if (this.isDevelopment) {
      console.warn(formatted, error || '');
    } else {
      console.warn(formatted);
      this.sendToLoggingService('warn', message, { ...context, error: error?.message });
    }

    // Send warnings to error tracking in production
    if (!this.isDevelopment && this.errorTrackingService && error) {
      this.errorTrackingService.captureException(error, {
        level: 'warning',
        extra: context,
      });
    }
  }

  /**
   * Log error message
   */
  error(message: string, error?: Error | unknown, context?: LogContext): void {
    if (!this.shouldLog(LogLevel.ERROR)) return;
    
    const errorObj = error instanceof Error ? error : new Error(String(error));
    const formatted = this.formatMessage('ERROR', message, context);
    
    // Always log errors
    console.error(formatted, errorObj);
    
    // Send to logging service
    this.sendToLoggingService('error', message, {
      ...context,
      error: errorObj.message,
      stack: errorObj.stack,
    });

    // Send to error tracking service
    if (this.errorTrackingService) {
      this.errorTrackingService.captureException(errorObj, {
        level: 'error',
        extra: {
          message,
          ...context,
        },
      });
    }
  }

  /**
   * Send logs to external logging service (Firebase Analytics, etc.)
   */
  private sendToLoggingService(level: string, message: string, context?: LogContext): void {
    // In production, you can send to Firebase Analytics or other logging services
    // This is a placeholder for future implementation
    try {
      // Example: Firebase Analytics
      // analytics().logEvent('app_log', { level, message, ...context });
    } catch (error) {
      // Silently fail to avoid logging loops
    }
  }

  /**
   * Log performance metric
   */
  performance(metric: string, duration: number, context?: LogContext): void {
    if (!this.shouldLog(LogLevel.INFO)) return;
    
    const message = `Performance: ${metric} took ${duration}ms`;
    this.info(message, { ...context, duration, metric });
  }

  /**
   * Log network request
   */
  network(method: string, url: string, status?: number, duration?: number, context?: LogContext): void {
    const level = status && status >= 400 ? LogLevel.ERROR : LogLevel.INFO;
    const message = `${method} ${url} ${status ? `(${status})` : ''} ${duration ? `[${duration}ms]` : ''}`;
    
    if (level === LogLevel.ERROR) {
      this.error(message, undefined, context);
    } else {
      this.info(message, context);
    }
  }
}

// Export singleton instance
export const logger = new Logger();

// Export convenience functions
export const logDebug = (message: string, context?: LogContext) => logger.debug(message, context);
export const logInfo = (message: string, context?: LogContext) => logger.info(message, context);
export const logWarn = (message: string, context?: LogContext, error?: Error) => logger.warn(message, context, error);
export const logError = (message: string, error?: Error | unknown, context?: LogContext) => logger.error(message, error, context);
export const logPerformance = (metric: string, duration: number, context?: LogContext) => logger.performance(metric, duration, context);
export const logNetwork = (method: string, url: string, status?: number, duration?: number, context?: LogContext) => logger.network(method, url, status, duration, context);

// Lightweight UI debug switch (opt-in via env, works in preview)
export const isUiDebugLoggingEnabled = (): boolean =>
  process.env.EXPO_PUBLIC_UI_DEBUG_LOGS === 'true';

// Canonical gate for internal debug surfaces/logging.
export const isInternalDebugBuild = (): boolean =>
  __DEV__ || isUiDebugLoggingEnabled();

